/**
 * Wording in the iOS app that can't read as lending (App Review 3.2.1(viii),
 * Nov 2026: "the app facilitates loan or credit applications").
 *
 * Defera is layaway: customers pay before they fly and nothing is owed after
 * the trip. But slogans like "Buy Now, Pay Later platform", "Everyone is
 * approved" and "No credit check, no interest" read like a credit product to a
 * reviewer. The wording is spread over pages, help articles and checkout, so
 * in the app only, visible text is rewritten as it renders. The website keeps
 * its wording: on the web nothing here runs.
 *
 * Statements that Defera doesn't run credit checks or lend ("we don't require
 * a credit check", "we're not a lender") stay: they say what Defera isn't.
 */

/** Whole text nodes that are only a slogan or a label. */
const WHOLE = [
  [/^no credit checks?( required)?(\.?)$/i, 'Pay before you fly$2'],
  [/^credit checks?$/i, 'Credit required'],
  [/^trip approved\.$/i, 'Trip confirmed.'],
  [/^everyone(?: is|'s|’s| gets)? approved\.?$/i, ''],
  [/^is there a credit check\?$/i, 'Do I need good credit?'],
  [/^is there really no credit check\?$/i, 'Do I need good credit?'],
  [/^yes\. there's no credit check[—-]everyone is approved\.$/i, "No. You don't need good credit to book with Defera."],
];

/** Phrases inside longer text, applied in order. */
const PHRASES = [
  [/\bbuy now,? pay later platform for travel\b/gi, 'flight layaway platform'],
  [/\bbook now,? pay later\b/gi, 'book now, pay over time'],
  [/\bbuy now,? pay later\b/gi, 'pay-over-time'],
  [/\bBNPL\b/g, 'pay-over-time'],
  [/\binstant approval\b/gi, 'instant booking'],
  [/\b(booking|trip) (is|gets|was) approved\b/gi, '$1 $2 confirmed'],
  [/\s*(?:[—-]\s*)?everyone(?: is|'s|’s| gets)? approved\b\.?/gi, ''],
  // "Is there a credit check or interest?" → "Is there interest?"
  [/\ba credit check or (?=interest)/gi, ''],
  // "price protection, no credit check and no interest" → "price protection and no interest"
  [/,\s*no credit checks? and (?=no interest)/gi, ' and '],
  // "No credit check, no interest" / "no credit check and no interest" → "no interest"
  [/\b(?:no|zero) credit checks?(?: required)?[.,]?\s+(?:and |or )?(no|zero|0%)\b/gi, '$1'],
  [/\s+with no credit checks?\b/gi, ''],
  [/\s+and no credit checks? booking\b/gi, ''],
  // "…, and no credit checks." / "No interest. No credit check." → drop the tail
  [/(?:,\s*(?:and\s+)?|\.\s+|\s+and\s+)no credit checks?(?: required)?(?=[.,;!)]|$)/gi, ''],
  [/\btravel financing leaves\b/gi, 'travel payment options leave'],
  [/\btravel financing\b/gi, 'travel payment options'],
  [/\bfair financing\b/gi, 'fair ways to pay'],
];

/** A replacement that starts a sentence starts with a capital, like the words it replaced. */
function matchCase(original, replacement, before) {
  if (!replacement) return replacement;
  const startsSentence = /(^|[.!?:]\s+|["'“‘]\s*)$/.test(before) && !/:\s+$/.test(before);
  const first = original.trimStart()[0];
  if (startsSentence && first && first !== first.toLowerCase()) {
    return replacement[0].toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/** The app's wording for one piece of visible text. */
export function rewordForAppStore(text) {
  if (typeof text !== 'string' || !/credit check|pay later|BNPL|approv|financing/i.test(text)) return text;
  const lead = text.match(/^\s*/)[0];
  const trail = text.match(/\s*$/)[0];
  const core = text.trim();
  for (const [re, to] of WHOLE) {
    if (re.test(core)) return lead + core.replace(re, to) + trail;
  }
  let out = text;
  for (const [re, to] of PHRASES) {
    out = out.replace(re, (...m) => {
      const match = m[0];
      const groups = m.slice(1, -2);
      const offset = m[m.length - 2];
      const filled = to.replace(/\$(\d)/g, (_, i) => groups[i - 1] ?? '');
      return matchCase(match, filled, out.slice(0, offset));
    });
  }
  return out;
}

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'NOSCRIPT']);

function rewordNode(node) {
  if (node.nodeType !== 3) return;
  const parent = node.parentNode;
  if (!parent || SKIP_TAGS.has(parent.nodeName) || parent.isContentEditable) return;
  const next = rewordForAppStore(node.nodeValue);
  if (next !== node.nodeValue) node.nodeValue = next;
}

function rewordTree(root, doc) {
  if (root.nodeType === 3) return rewordNode(root);
  if (root.nodeType !== 1 || SKIP_TAGS.has(root.nodeName)) return;
  const walker = doc.createTreeWalker(root, 4);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) rewordNode(n);
}

/** Reword the page now and whenever React renders new text. Returns a stop function. */
export function installAppStoreWording({ doc = document, MutationObserverImpl = globalThis.MutationObserver } = {}) {
  const root = doc.body || doc.documentElement;
  rewordTree(root, doc);
  if (!MutationObserverImpl) return () => {};
  const observer = new MutationObserverImpl((records) => {
    for (const r of records) {
      if (r.type === 'characterData') rewordNode(r.target);
      else r.addedNodes.forEach((n) => rewordTree(n, doc));
    }
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true });
  return () => observer.disconnect();
}
